import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  NavLink,
} from "reactstrap";
import { LuBuilding2, LuCheck, LuChevronDown } from "react-icons/lu";
import { IoLogOutOutline } from "react-icons/io5";
import { CgWebsite } from "react-icons/cg";
import { BiSupport } from "react-icons/bi";
import { RiLockPasswordLine } from "react-icons/ri";
import { useGetAccount, useUpdateSelectedTenant } from "api/account/service";
import { useQueryClient } from "@tanstack/react-query";
import { clearAuthSession } from "utils/authSession";
import {
  THEME2_NAVBAR_ACCOUNT_TEXT_WEIGHT,
  THEME2_NAVBAR_ACCOUNT_TEXT_WEIGHT_ACTIVE,
} from "./adminNavbarTheme2Appearance";

/** Tema 2 sol menü aktif satırı ile aynı overlay */
const T2_ACCOUNT_MENU_ROUTE_ACTIVE_BG_DARK = "rgba(0, 0, 0, 0.2)";
const T2_ACCOUNT_MENU_ROUTE_ACTIVE_BG_LIGHT = "rgba(0, 0, 0, 0.06)";

const isPathActiveRoute = (pathname, basePath) =>
  pathname === basePath || pathname.startsWith(`${basePath}/`);

const getT2AccountRouteMenuItemPresentation = ({
  isRouteActive,
  isHovered,
  navbarLightSurface,
  menuText,
  t2AccountMenuItemHoverFg,
}) => {
  if (isRouteActive) {
    return {
      activeClass: "dtiq-theme2-account-menu-item--active-route",
      color: navbarLightSurface ? "#000000" : "#ffffff",
      backgroundColor: navbarLightSurface
        ? T2_ACCOUNT_MENU_ROUTE_ACTIVE_BG_LIGHT
        : T2_ACCOUNT_MENU_ROUTE_ACTIVE_BG_DARK,
      fontWeight: 600,
    };
  }
  if (isHovered) {
    return {
      activeClass: "",
      color: t2AccountMenuItemHoverFg,
      backgroundColor: navbarLightSurface ? "#f0f0f0" : "#404954",
      fontWeight: undefined,
    };
  }
  return {
    activeClass: "",
    color: menuText,
    backgroundColor: undefined,
    fontWeight: undefined,
  };
};

const getInitials = (str) => {
  if (!str) return "";
  const parts = str.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0];
  return parts[0][0] + parts[parts.length - 1][0];
};

function isSameTenantId(selected, tenantId) {
  if (selected == null || tenantId == null) return false;
  if (selected === tenantId) return true;
  if (String(selected) === String(tenantId)) return true;
  const ns = Number(selected);
  const nt = Number(tenantId);
  return Number.isFinite(ns) && Number.isFinite(nt) && ns === nt;
}

/** Same behavior as former navbar user menu; `sidebar` = Theme 2 footer (opens upward). */
const AccountUserMenu = ({
  variant = "navbar",
  closeSidebar,
  closeMobileNavbar,
  mobileNavbarCollapseOpen,
  lightNavbarSurface = false,
  hideLanguageInUserMenu = false,
  helpInUserMenu = false,
  onHelpToggle,
  theme2Shell = false,
  /** Tema 2 üst bar metni: sidebar ile aynı CSS değişkenleri (navbar şerit yüksekliği lg+ SCSS ile sabit). */
  applyShellNavTypographyVars = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { data: account } = useGetAccount();
  const { mutate: updateTenant } = useUpdateSelectedTenant();
  const queryClient = useQueryClient();
  const [userDropdownOpen, setUserDropdownOpen] = React.useState(false);
  const [languageOpen, setLanguageOpen] = React.useState(false);
  const [selectedLanguage, setSelectedLanguage] = React.useState(i18n.language || "en");
  const [hoveredTenantId, setHoveredTenantId] = React.useState(null);
  const [themeSettingsHovered, setThemeSettingsHovered] = React.useState(false);
  const [changePasswordHovered, setChangePasswordHovered] = React.useState(false);
  const [helpHovered, setHelpHovered] = React.useState(false);
  const [logoutHovered, setLogoutHovered] = React.useState(false);
  const logoutColor = logoutHovered ? "#c82333" : "#dc3545";

  React.useEffect(() => {
    setSelectedLanguage(i18n.language || "en");
  }, [i18n.language]);

  React.useEffect(() => {
    if (!userDropdownOpen) {
      setHoveredTenantId(null);
      setThemeSettingsHovered(false);
      setChangePasswordHovered(false);
      setHelpHovered(false);
      setLogoutHovered(false);
    }
  }, [userDropdownOpen]);

  React.useEffect(() => {
    if (mobileNavbarCollapseOpen === false) {
      setUserDropdownOpen(false);
      setLanguageOpen(false);
    }
  }, [mobileNavbarCollapseOpen]);

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    setSelectedLanguage(langCode);
    setLanguageOpen(false);
  };

  const handleLogout = () => {
    clearAuthSession(queryClient);
    navigate("/login", { replace: true });
  };

  const afterMenuAction = () => {
    setUserDropdownOpen(false);
    if (typeof closeMobileNavbar === "function") closeMobileNavbar();
    if (typeof closeSidebar === "function") closeSidebar();
  };

  const isSidebar = variant === "sidebar";
  const navbarLightSurface = lightNavbarSurface && !isSidebar;
  const t2 = theme2Shell;
  const menuText = navbarLightSurface ? "#1f2430" : "rgba(255, 255, 255, 0.92)";
  /** Tema 2 hesap menüsü: açık temada hover (#f0f0f0) için koyu metin; siyah temada (#404954) için açık metin */
  const t2AccountMenuItemHoverText = "rgba(50, 50, 93, 0.92)";
  const t2AccountMenuItemHoverFg = navbarLightSurface ? t2AccountMenuItemHoverText : "rgba(255, 255, 255, 0.95)";
  /** Tema 2: koyu zemin (sidebar / koyu navbar) beyaz; açık navbar yüzeyinde mevcut gri (#8e929d) */
  const t2SurfacePrimaryText = navbarLightSurface ? menuText : "#ffffff";
  const t2NavbarAccountTextWeight =
    t2 && !isSidebar && !navbarLightSurface
      ? userDropdownOpen
        ? THEME2_NAVBAR_ACCOUNT_TEXT_WEIGHT_ACTIVE
        : THEME2_NAVBAR_ACCOUNT_TEXT_WEIGHT
      : null;
  /** Tema 2: seçili şirket satırı — açık menüde siyah, koyu menüde beyaz */
  const t2SelectedTenantFg = navbarLightSurface ? "#000000" : "#ffffff";
  const shellNavTypoEnabled = applyShellNavTypographyVars && t2 && !isSidebar;
  /** Tema 1 navbar: koyu şerit = beyaz metin; mobil collapse `bg-white` = koyu metin */
  const t1NavbarPrimaryText = navbarLightSurface ? "#1f2430" : "#ffffff";
  const t1NavbarSecondaryText = navbarLightSurface ? "#3f4654" : "rgba(255, 255, 255, 0.85)";
  const lightTenantText = "#1f2430";
  const lightUserText = "#4a5160";

  const selectedTenantRecord =
    account && !account.is_super_admin && Array.isArray(account.tenants) && account.selected_tenant != null
      ? account.tenants.find((tn) => isSameTenantId(account.selected_tenant, tn.id))
      : null;
  const selectedCompanyLabel = selectedTenantRecord
    ? `${selectedTenantRecord.code}-${selectedTenantRecord.name}`
    : null;
  const accountDisplayName =
    account?.user_detail?.full_name ||
    account?.full_name ||
    account?.username ||
    "";

  const isThemeSettingsRoute = isPathActiveRoute(location.pathname, "/sidebar-appearance");
  const isChangePasswordRoute = isPathActiveRoute(location.pathname, "/change-password");

  const themeSettingsMenu = getT2AccountRouteMenuItemPresentation({
    isRouteActive: t2 && isThemeSettingsRoute,
    isHovered: themeSettingsHovered,
    navbarLightSurface,
    menuText,
    t2AccountMenuItemHoverFg,
  });

  const changePasswordMenu = getT2AccountRouteMenuItemPresentation({
    isRouteActive: t2 && isChangePasswordRoute,
    isHovered: changePasswordHovered,
    navbarLightSurface,
    menuText,
    t2AccountMenuItemHoverFg,
  });

  return (
    <Dropdown
      isOpen={userDropdownOpen}
      toggle={() => setUserDropdownOpen((v) => !v)}
      nav={!isSidebar}
      direction={isSidebar ? "up" : "down"}
      className={[
        isSidebar ? "w-100 sidebar-shell-theme-2-account-root" : "",
        !isSidebar && t2 ? "admin-navbar-shell-theme-2-account-root" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <DropdownToggle
        color="default"
        data-toggle="dropdown"
        nav={!isSidebar}
        className={t2 ? "dtiq-theme2-account-menu-trigger" : undefined}
        style={
          isSidebar
            ? {
                justifyContent: "flex-start",
                width: "100%",
                padding: "10px 12px",
                backgroundColor: userDropdownOpen ? "rgba(255, 255, 255, 0.12)" : "transparent",
                border: "1px solid rgba(255, 255, 255, 0.18)",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }
            : {
                justifyContent: "space-between",
                padding: "5px 12px",
                backgroundColor: navbarLightSurface
                  ? userDropdownOpen
                    ? "rgba(0, 0, 0, 0.08)"
                    : "transparent"
                  : userDropdownOpen
                    ? "rgba(255, 255, 255, 0.15)"
                    : "transparent",
                transition: "all 0.2s ease",
              }
        }
        onClick={(e) => e.preventDefault()}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "row",
            width: "100%",
            gap: 8,
            minWidth: 0,
          }}
        >
          <div
            className="photo flex-shrink-0"
            style={{
              width: 32,
              height: 32,
              background:
                userDropdownOpen && t2 && !navbarLightSurface
                  ? "#5a6272"
                  : userDropdownOpen && !t2
                    ? "#1d8cf8"
                    : "#6c757d",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontWeight: "bold",
              fontSize: 14,
              transition: "all 0.2s ease",
            }}
          >
            {!account ? "" : getInitials(account.username || "").toUpperCase()}
          </div>
          {t2 ? (
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                alignItems: "flex-start",
                ...(shellNavTypoEnabled
                  ? { lineHeight: "var(--sidebar-shell-theme-2-nav-line-height, 1.65)" }
                  : {}),
              }}
            >
              <span
                className="sidebar-shell-theme-2-account-company"
                style={{
                  fontWeight: t2NavbarAccountTextWeight ?? "700",
                  color: navbarLightSurface ? lightTenantText : t2SurfacePrimaryText,
                  transition: "all 0.2s ease",
                  ...(shellNavTypoEnabled
                    ? {
                        fontSize: "var(--sidebar-shell-theme-2-nav-font-size, 14px)",
                        lineHeight: "calc(var(--sidebar-shell-theme-2-nav-line-height, 1.65) * 0.95)",
                        width: "100%",
                      }
                    : { fontSize: "14px", lineHeight: 1.15, width: "100%" }),
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {selectedCompanyLabel || accountDisplayName}
              </span>
              {accountDisplayName ? (
                <span
                  className="sidebar-shell-theme-2-account-username"
                  style={{
                    fontWeight: t2NavbarAccountTextWeight ?? "500",
                    color: navbarLightSurface ? lightUserText : t2SurfacePrimaryText,
                    ...(shellNavTypoEnabled
                      ? {
                          fontSize: "calc(var(--sidebar-shell-theme-2-nav-font-size, 14px) * 0.82)",
                          lineHeight: "calc(var(--sidebar-shell-theme-2-nav-line-height, 1.65) * 0.78)",
                          width: "100%",
                        }
                      : { fontSize: "12px", lineHeight: 1.05, width: "100%" }),
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    opacity: 0.8,
                  }}
                >
                  {accountDisplayName}
                </span>
              ) : null}
            </div>
          ) : (
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: accountDisplayName ? 1 : 0,
                alignItems: "flex-start",
              }}
            >
              <span
                className="sidebar-shell-theme-2-account-company"
                style={{
                  fontWeight: "700",
                  color: userDropdownOpen ? "#1d8cf8" : lightTenantText,
                  transition: "all 0.2s ease",
                  fontSize: "14px",
                  lineHeight: 1.1,
                  width: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {selectedCompanyLabel || accountDisplayName}
              </span>
              {accountDisplayName ? (
                <span
                  className="sidebar-shell-theme-2-account-username"
                  style={{
                    fontWeight: "500",
                    color: userDropdownOpen ? "#1d8cf8" : lightUserText,
                    fontSize: "12px",
                    lineHeight: 1.02,
                    width: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    opacity: 0.85,
                  }}
                >
                  {accountDisplayName}
                </span>
              ) : null}
            </div>
          )}
          <LuChevronDown
            size={16}
            className="flex-shrink-0 d-none d-lg-block"
            style={{
              transition: "transform 0.2s ease, color 0.2s ease",
              transform: userDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
              color: t2
                ? (navbarLightSurface ? lightUserText : t2SurfacePrimaryText)
                : userDropdownOpen
                  ? "#1d8cf8"
                  : navbarLightSurface
                    ? lightUserText
                    : "#ffffff",
            }}
            aria-hidden
          />
        </div>
      </DropdownToggle>
      <DropdownMenu
        className={`dropdown-navbar dropdown-black${
          hideLanguageInUserMenu && !isSidebar ? " admin-navbar-shell-theme-2-account-dropdown" : ""
        }`}
        right={!isSidebar}
        tag="ul"
        style={{
          marginTop: isSidebar ? 0 : t2 ? "4px" : "10px",
          marginBottom: isSidebar ? "8px" : undefined,
          ...(t2 ? { color: menuText } : {}),
        }}
      >
        <div style={{ maxHeight: "150px", overflowY: "auto" }}>
          {!account?.is_super_admin &&
            account?.tenants?.map((tenant) => {
              if (t2) {
                const tenantRowHovered = hoveredTenantId === tenant.id;
                const isSelectedTenant = isSameTenantId(account?.selected_tenant, tenant.id);
                const tenantRowColor = isSelectedTenant
                  ? t2SelectedTenantFg
                  : tenantRowHovered
                    ? t2AccountMenuItemHoverFg
                    : menuText;
                return (
                  <NavLink tag="li" key={tenant.id}>
                    <DropdownItem
                      className="nav-item text-capitalize"
                      style={{ color: tenantRowColor }}
                      onMouseEnter={() => setHoveredTenantId(tenant.id)}
                      onMouseLeave={() => setHoveredTenantId(null)}
                      onClick={() => {
                        if (isSameTenantId(account?.selected_tenant, tenant.id)) return;
                        updateTenant(
                          { selected_tenant: Number(tenant.id) },
                          {
                            onSuccess: () => {
                              window.location.reload();
                            },
                          }
                        );
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                          justifyContent: "space-between",
                        }}
                      >
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            minWidth: 0,
                            fontWeight: isSelectedTenant ? "bold" : "normal",
                            color: tenantRowColor,
                          }}
                        >
                          <LuBuilding2
                            size={18}
                            className="flex-shrink-0"
                            style={{ color: "currentColor" }}
                            aria-hidden
                          />
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {tenant.code + "-" + tenant.name}
                          </span>
                        </span>
                        {isSelectedTenant && (
                          <LuCheck
                            size={18}
                            className="flex-shrink-0"
                            style={{
                              color: tenantRowColor,
                              marginLeft: "10px",
                            }}
                            aria-hidden
                          />
                        )}
                      </div>
                    </DropdownItem>
                  </NavLink>
                );
              }
              return (
                <NavLink tag="li" key={tenant.id}>
                  <DropdownItem
                    className="nav-item text-capitalize"
                    onClick={() => {
                      if (isSameTenantId(account?.selected_tenant, tenant.id)) return;
                      updateTenant(
                        { selected_tenant: Number(tenant.id) },
                        {
                          onSuccess: () => {
                            window.location.reload();
                          },
                        }
                      );
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: isSameTenantId(account?.selected_tenant, tenant.id) ? "bold" : "normal",
                        }}
                      >
                        {tenant.code + "-" + tenant.name}
                      </span>
                      {isSameTenantId(account?.selected_tenant, tenant.id) && (
                        <LuCheck
                          size={18}
                          style={{
                            color: "#1d8cf8",
                            marginLeft: "10px",
                          }}
                        />
                      )}
                    </div>
                  </DropdownItem>
                </NavLink>
              );
            })}
        </div>
        <NavLink tag="li">
          <DropdownItem divider tag="li" />
          <DropdownItem
            className={["nav-item", themeSettingsMenu.activeClass].filter(Boolean).join(" ")}
            style={
              t2
                ? {
                    color: themeSettingsMenu.color,
                    backgroundColor: themeSettingsMenu.backgroundColor,
                    fontWeight: themeSettingsMenu.fontWeight,
                  }
                : isThemeSettingsRoute
                  ? { fontWeight: 600 }
                  : undefined
            }
            onMouseEnter={() => t2 && setThemeSettingsHovered(true)}
            onMouseLeave={() => t2 && setThemeSettingsHovered(false)}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              afterMenuAction();
              navigate("/sidebar-appearance");
            }}
          >
            {t2 ? (
              <span className="d-flex align-items-center">
                <CgWebsite
                  size={18}
                  className="flex-shrink-0"
                  style={{
                    color: themeSettingsMenu.color,
                    marginRight: 10,
                  }}
                  aria-hidden
                />
                <span style={{ color: themeSettingsMenu.color, fontWeight: themeSettingsMenu.fontWeight }}>
                  {t("routes.sidebarAppearance")}
                </span>
              </span>
            ) : (
              t("routes.sidebarAppearance")
            )}
          </DropdownItem>
          <DropdownItem
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              afterMenuAction();
              navigate("/change-password");
            }}
            className={["nav-item", changePasswordMenu.activeClass].filter(Boolean).join(" ")}
            style={
              t2
                ? {
                    color: changePasswordMenu.color,
                    backgroundColor: changePasswordMenu.backgroundColor,
                    fontWeight: changePasswordMenu.fontWeight,
                  }
                : isChangePasswordRoute
                  ? { fontWeight: 600 }
                  : undefined
            }
            onMouseEnter={() => t2 && setChangePasswordHovered(true)}
            onMouseLeave={() => t2 && setChangePasswordHovered(false)}
          >
            {t2 ? (
              <span className="d-flex align-items-center">
                <RiLockPasswordLine
                  size={18}
                  className="flex-shrink-0"
                  style={{
                    color: changePasswordMenu.color,
                    marginRight: 10,
                  }}
                  aria-hidden
                />
                <span style={{ color: changePasswordMenu.color, fontWeight: changePasswordMenu.fontWeight }}>
                  {t("navbar.changePassword")}
                </span>
              </span>
            ) : (
              t("navbar.changePassword")
            )}
          </DropdownItem>
          {helpInUserMenu && typeof onHelpToggle === "function" && (
            <DropdownItem
              className="nav-item"
              style={t2 ? { color: helpHovered ? t2AccountMenuItemHoverFg : menuText } : { color: menuText }}
              onMouseEnter={() => t2 && setHelpHovered(true)}
              onMouseLeave={() => t2 && setHelpHovered(false)}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                afterMenuAction();
                onHelpToggle(true);
              }}
            >
              <span className="d-flex align-items-center">
                <BiSupport
                  size={18}
                  className="flex-shrink-0"
                  style={{
                    color: t2 ? (helpHovered ? t2AccountMenuItemHoverFg : menuText) : menuText,
                    marginRight: 10,
                  }}
                  aria-hidden
                />
                <span style={t2 ? { color: helpHovered ? t2AccountMenuItemHoverFg : menuText } : undefined}>
                  {t("navbar.help")}
                </span>
              </span>
            </DropdownItem>
          )}
        </NavLink>
        {!hideLanguageInUserMenu && (
          <NavLink tag="li">
            <div
              className="nav-item"
              onClick={(e) => {
                e.stopPropagation();
                setLanguageOpen(!languageOpen);
              }}
              style={{
                cursor: "pointer",
                padding: "8px 16px",
                display: "block",
                color: t2 ? menuText : "#9B9B9B",
                fontSize: "13px",
                marginLeft: "10px",
              }}
            >
              {t("navbar.language")} ({selectedLanguage.substring(0, 2).toUpperCase()})
            </div>
            {languageOpen && (
              <div
                style={{
                  maxHeight: "150px",
                  overflowY: "auto",
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  margin: "4px 0",
                }}
              >
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    changeLanguage("en");
                  }}
                  style={{
                    padding: "8px 16px",
                    cursor: "pointer",
                    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                    color: t2 ? menuText : "white",
                  }}
                >
                  {t("languages.english")}
                </div>
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    changeLanguage("es");
                  }}
                  style={{
                    padding: "8px 16px",
                    cursor: "pointer",
                    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                    color: t2 ? menuText : "white",
                  }}
                >
                  {t("languages.spanish")}
                </div>
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    changeLanguage("tr");
                  }}
                  style={{
                    padding: "8px 16px",
                    cursor: "pointer",
                    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                    color: t2 ? menuText : "white",
                  }}
                >
                  {t("languages.turkish")}
                </div>
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    changeLanguage("de");
                  }}
                  style={{
                    padding: "8px 16px",
                    cursor: "pointer",
                    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                    color: t2 ? menuText : "white",
                  }}
                >
                  {t("languages.german")}
                </div>
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    changeLanguage("pt");
                  }}
                  style={{
                    padding: "8px 16px",
                    cursor: "pointer",
                    border: "none",
                    color: t2 ? menuText : "white",
                  }}
                >
                  {t("languages.portuguese")}
                </div>
              </div>
            )}
          </NavLink>
        )}
        <DropdownItem divider tag="li" />
        <NavLink tag="li">
          <DropdownItem
            className="nav-item"
            style={t2 ? { color: logoutColor } : { color: logoutColor }}
            onMouseEnter={() => t2 && setLogoutHovered(true)}
            onMouseLeave={() => t2 && setLogoutHovered(false)}
            onClick={handleLogout}
          >
            {t2 ? (
              <span className="d-flex align-items-center">
                <IoLogOutOutline
                  size={18}
                  className="flex-shrink-0"
                  style={{
                    color: logoutColor,
                    marginRight: 10,
                  }}
                  aria-hidden
                />
                <span style={{ color: logoutColor }}>{t("navbar.logout")}</span>
              </span>
            ) : (
              t("navbar.logout")
            )}
          </DropdownItem>
        </NavLink>
      </DropdownMenu>
    </Dropdown>
  );
};

export default AccountUserMenu;
